<script setup lang="ts" generic="T extends object">
  import { computed } from "vue";
  import { DataTable, type DataTableColumn } from "@/components/episto-ui/data-table";

  const props = withDefaults(
    defineProps<{
      items: T[];
      columns: DataTableColumn<T>[];
      itemKey?: keyof T | string;
      currentPage?: number;
      total?: number;
      pageSize?: number;
      loading?: boolean;
      skeletonRows?: number;
      emptyMessage?: string;
      actionsClass?: string;
      expandedKeys?: (string | number)[];
      rowDataCy?: string;
      sortColumn?: string;
      sortDirection?: "asc" | "desc";
      compact?: boolean;
      resizable?: boolean;
      selectedKey?: string | number | null;
      height?: string;
      reorderableColumns?: boolean;
      reorderableRows?: boolean | ((item: T) => boolean);
    }>(),
    {
      itemKey: "id",
      currentPage: 1,
      total: 0,
      pageSize: 20,
      loading: false,
      skeletonRows: 10,
      emptyMessage: "No items found.",
      actionsClass: "w-[70px]",
      expandedKeys: undefined,
      rowDataCy: undefined,
      sortColumn: "",
      sortDirection: "asc",
      compact: false,
      resizable: false,
      selectedKey: null,
      height: "auto",
      reorderableColumns: false,
      reorderableRows: false,
    },
  );

  const emit = defineEmits<{
    "row-click": [item: T, event: MouseEvent | KeyboardEvent];
    "page-change": [page: number];
    sort: [column: string];
    "reorder-columns": [keys: string[]];
    "reorder-rows": [items: T[]];
  }>();

  defineSlots<{
    [name: `cell-${string}`]: (props: { item: T; value: unknown }) => unknown;
    actions: (props: { item: T }) => unknown;
    expanded: (props: { item: T }) => unknown;
    empty: () => unknown;
  }>();

  const totalPages = computed(() => Math.max(1, Math.ceil(props.total / props.pageSize)));
</script>

<template>
  <DataTable
    :items="items"
    :columns="columns"
    :item-key="itemKey"
    :current-page="currentPage"
    :total-pages="totalPages"
    :items-per-page="pageSize"
    :loading="loading"
    :skeleton-rows="skeletonRows"
    :empty-message="emptyMessage"
    :actions-class="actionsClass"
    :expanded-keys="expandedKeys"
    :row-data-cy="rowDataCy"
    :sort-column="sortColumn"
    :sort-direction="sortDirection"
    :table-class="compact ? '[&_td:not([data-slot=detail])]:py-2!' : ''"
    :resizable="resizable"
    :selected-item-id="selectedKey"
    :height="height"
    :reorderable-columns="reorderableColumns"
    :reorderable-rows="reorderableRows"
    variant="dense"
    fixed-widths
    clickable-rows
    @row-click="(item, event) => emit('row-click', item, event)"
    @page-change="emit('page-change', $event)"
    @sort="emit('sort', $event)"
    @reorder-columns="emit('reorder-columns', $event)"
    @reorder-rows="emit('reorder-rows', $event)">
    <template
      v-for="name in (Object.keys($slots) as `cell-${string}`[])"
      :key="name"
      #[name]="slotProps">
      <slot
        :name="name"
        v-bind="slotProps ?? {}" />
    </template>
  </DataTable>
</template>
